import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Landmark, RefreshCw } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import { LoadingState } from '../../../components/AsyncStates';
import YearMonthPicker from '../../../components/yearMonthPicker';
import { useComplianceCountry } from '../../../hooks/useComplianceCountry';
import {
  PensionCoverageView,
  GpssaRemittanceView,
  MonthlyPensionContributionView,
  GpssaComplianceSummaryView,
  ComplianceDashboardView,
} from '../../../models/payroll';
import { getComplianceDashboard } from '../../../api/Payroll/compliance';
import {
  getGpssaCoverage,
  getGpssaRemittance,
  getContributions,
  getGpssaComplianceSummary,
} from '../../../api/Payroll/gpssa';
import { PensionTab } from '../ComplianceCenter/tabs/PensionTab';

const PensionCenter: React.FC = () => {
  const { t } = useTranslation();
  const country = useComplianceCountry();

  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [dashboard, setDashboard] = useState<ComplianceDashboardView | null>(null);
  const [coverage, setCoverage] = useState<PensionCoverageView[]>([]);
  const [remittance, setRemittance] = useState<GpssaRemittanceView | null>(null);
  const [contributions, setContributions] = useState<MonthlyPensionContributionView[]>([]);
  const [summary, setSummary] = useState<GpssaComplianceSummaryView | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (country !== 'UAE') {
      setLoading(false);
      return;
    }
    try {
      const [dashRes, coverageRes, remittanceRes, contribRes, summaryRes] = await Promise.all([
        getComplianceDashboard(year, month),
        getGpssaCoverage(year, month),
        getGpssaRemittance(year, month),
        getContributions(year, month),
        getGpssaComplianceSummary(year, month),
      ]);
      if (dashRes.errors.length === 0) setDashboard(dashRes.payload);
      if (coverageRes.errors.length === 0) setCoverage(coverageRes.payload);
      if (remittanceRes.errors.length === 0) setRemittance(remittanceRes.payload);
      if (contribRes.errors.length === 0) setContributions(contribRes.payload);
      if (summaryRes.errors.length === 0) setSummary(summaryRes.payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [year, month, t, country]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const title =
    country === 'KSA'
      ? t('payroll.pension.titleKsa', 'GOSI Contributions')
      : t('payroll.pension.titleUae', 'Pension — GPSSA & ADPF');

  return (
    <PageLayout title={title} icon={Landmark} accentColor="blue" maxWidth="1400px">
      <div className="space-y-6" data-testid="pension-center">
        <div className="flex items-center gap-4 flex-wrap">
          <YearMonthPicker
            date={new Date(year, month - 1, 1)}
            onChange={d => {
              setYear(d.getFullYear());
              setMonth(d.getMonth() + 1);
            }}
          />
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-input rounded-md hover:bg-muted transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            {t('common.refresh', 'Refresh')}
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
        )}
        {info && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md text-emerald-700 text-sm">
            {info}
          </div>
        )}

        {loading ? (
          <LoadingState title={t('common.loading')} size="md" />
        ) : (
          <PensionTab
            year={year}
            month={month}
            coverage={coverage}
            remittance={remittance}
            contributions={contributions}
            summary={summary}
            basicSalaryHealthy={dashboard?.basicSalaryInvariantHealthy}
            basicSalaryConfigCount={dashboard?.basicSalaryConfigCount}
            onContributionsGenerated={items => {
              setContributions(items);
              fetchData();
            }}
            onError={msg => {
              setInfo(null);
              setError(msg);
            }}
            onInfo={msg => {
              setError(null);
              setInfo(msg);
            }}
          />
        )}
      </div>
    </PageLayout>
  );
};

export default PensionCenter;
